const FACEBOOK_HOST_PATTERN = /^(?:www\.|m\.|web\.)?facebook\.com$|^fb\.com$|^fb\.watch$/i;

const FACEBOOK_RESERVED_SEGMENTS = new Set([
    'reel',
    'reels',
    'share',
    'sharer',
    'sharer.php',
    'watch',
    'groups',
    'group',
    'events',
    'event',
    'photo',
    'photos',
    'video',
    'videos',
    'posts',
    'people',
    'pages',
    'gaming',
    'marketplace',
    'stories',
    'live',
    'notes',
    'profile.php',
    'login',
    'hashtag',
    'plugins',
    'dialog',
    'help',
    'legal',
    'privacy',
    'policies',
    'business',
    'ads',
    'l.php',
]);

const PAGE_SLUG_PATTERN = /^[a-z0-9.]+$/i;

export function isFacebookHost(hostname: string): boolean {
    return FACEBOOK_HOST_PATTERN.test(hostname.toLowerCase().replace(/^www\./, ''));
}

export function isFacebookDomain(domain: string | null): boolean {
    if (!domain) return false;
    return isFacebookHost(domain);
}

export function isFacebookUrl(value: string): boolean {
    const trimmed = value.trim();
    if (!trimmed) return false;
    try {
        const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
        return isFacebookHost(new URL(withProtocol).hostname);
    } catch {
        return false;
    }
}

export function normalizeFacebookPageSlug(value?: string): string | null {
    const trimmed = value?.trim().toLowerCase().replace(/^@/, '').replace(/\/+$/, '');
    if (!trimmed || !PAGE_SLUG_PATTERN.test(trimmed)) return null;
    if (FACEBOOK_RESERVED_SEGMENTS.has(trimmed)) return null;
    return trimmed;
}

export function facebookPageUrl(pageSlug: string): string {
    const slug = normalizeFacebookPageSlug(pageSlug);
    return slug ? `https://www.facebook.com/${slug}` : '';
}

export function extractFacebookPageSlug(urlOrSlug: string): string | null {
    if (!urlOrSlug?.trim()) return null;

    const trimmed = urlOrSlug.trim();
    if (!/[./]/.test(trimmed) && !trimmed.startsWith('@')) {
        return normalizeFacebookPageSlug(trimmed);
    }

    try {
        const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
        const parsed = new URL(withProtocol);
        if (!isFacebookHost(parsed.hostname)) return null;

        const segments = parsed.pathname.split('/').map((s) => s.trim()).filter(Boolean);
        if (segments.length === 0) return null;

        const first = segments[0].toLowerCase();
        if (FACEBOOK_RESERVED_SEGMENTS.has(first)) return null;

        return normalizeFacebookPageSlug(first);
    } catch {
        return null;
    }
}

export interface FacebookOwner {
    pageSlug: string;
    pageUrl: string;
}

export function resolveFacebookOwnerFromUrls(urls: readonly (string | undefined)[]): FacebookOwner | null {
    for (const url of urls) {
        if (!url?.trim()) continue;
        const pageSlug = extractFacebookPageSlug(url);
        if (pageSlug) {
            return { pageSlug, pageUrl: facebookPageUrl(pageSlug) };
        }
    }
    return null;
}

const FACEBOOK_VIDEO_PATH_KINDS = new Set(['reel', 'reels', 'video', 'videos']);
const FACEBOOK_NUMERIC_VIDEO_ID = /^\d{5,}$/;
const FACEBOOK_FB_WATCH_ID = /^[A-Za-z0-9_-]+$/;

export function extractFacebookVideoId(urlOrId: string): string | null {
    if (!urlOrId?.trim()) return null;

    const trimmed = urlOrId.trim();

    try {
        const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
        const parsed = new URL(withProtocol);
        const host = parsed.hostname.toLowerCase().replace(/^www\./, '');

        if (host === 'fb.watch' || host === 'fb.com') {
            const segments = parsed.pathname.split('/').map((s) => s.trim()).filter(Boolean);
            const token = segments[0]?.split('?')[0];
            if (token && FACEBOOK_FB_WATCH_ID.test(token)) return token;
        }

        if (!isFacebookHost(parsed.hostname)) return null;

        const vParam = parsed.searchParams.get('v')?.trim();
        if (vParam && FACEBOOK_NUMERIC_VIDEO_ID.test(vParam)) return vParam;

        const segments = parsed.pathname.split('/').map((s) => s.trim()).filter(Boolean);
        for (let index = 0; index < segments.length - 1; index += 1) {
            const kind = segments[index].toLowerCase();
            if (!FACEBOOK_VIDEO_PATH_KINDS.has(kind)) continue;
            const candidate = segments[index + 1].split('?')[0];
            if (FACEBOOK_NUMERIC_VIDEO_ID.test(candidate)) return candidate;
        }

        return null;
    } catch {
        return null;
    }
}
